import React, { useMemo } from 'react';
import { useAppSelector } from '@src/core/hooks';
import { Order, Sale } from '@src/core/buyerApiClient';
import { resolveVoucherSource, VoucherSource, useVoucherDownload } from '@src/features/vouchers';
import { orderHasVouchersSelector } from '@src/features/orders';
import { currentSaleHasVouchersSelector } from '@src/stores/sale';
import Confetti from '@src/assets/images/Confetti.svg';
import { Icon, Message, PaymentDone } from './styles';
import { MdCheck, MdOutlineCloudDownload } from 'react-icons/md';
import Button from '@src/components/ui2/Button';
import useTranslate from '@src/i18n/useTranslate';

type PaymentSuccessProps = {
  order?: Order;
  sale?: Sale;
};

const PaymentSuccess = (props: PaymentSuccessProps) => {
  const { sale, order } = props;

  const { i18n } = useTranslate();
  const { download, isDownloading } = useVoucherDownload();
  const saleHasVouchers = useAppSelector(currentSaleHasVouchersSelector);
  const orderHasVouchers = useAppSelector(orderHasVouchersSelector(order?.id ?? ''));
  const source = resolveVoucherSource({ sale, order });

  const hasVouchers = useMemo(() => {
    if (source === VoucherSource.Sale) {
      return saleHasVouchers;
    }

    if (source === VoucherSource.Order) {
      return orderHasVouchers;
    }

    return false;
  }, [orderHasVouchers, saleHasVouchers, source]);

  const loadingVouchersPdf = isDownloading(sale?.id ?? order?.id ?? '');

  const downloadVouchers = async () => {
    if (source === VoucherSource.Sale) {
      await download({ kind: 'sale', id: sale!.id });
    } else if (source === VoucherSource.Order) {
      await download({ kind: 'order', id: order!.id });
    }
  };

  return (
    <>
      <PaymentDone>
        {i18n('direct_link_payment_success_alert')} <MdCheck />
      </PaymentDone>

      <Icon>
        <Confetti />
      </Icon>

      <Message>{i18n('direct_link_payment_success_message')}</Message>

      {hasVouchers ? (
        <>
          <Message>{i18n('direct_link_payment_success_voucher_message')}</Message>

          <Button
            onClick={() => downloadVouchers()}
            fullWidth
            disabled={loadingVouchersPdf}
            loading={loadingVouchersPdf}
          >
            <MdOutlineCloudDownload />
            {i18n('direct_link_payment_success_download_vouchers')}
          </Button>
        </>
      ) : null}
    </>
  );
};

export default PaymentSuccess;
